import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Check, Star, Zap, Crown, ArrowRight, Loader2, Eye, EyeOff, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import CountdownTimer from '../components/CountdownTimer';
import AnnouncementTicker from '../components/AnnouncementTicker';
import api from '../services/api';
import { toast } from 'sonner';

const TIERS = {
  gameday_skill_engine: {
    name: 'GameDay Skill Engine',
    price: 19.99,
    period: 'mo',
    icon: Zap,
    color: '#60A5FA',
    features: [
      "Full access to The Combine — unlimited sports-based practice",
      "All 4 Core Subjects, grade-aligned to CA standards (4-9)",
      "Student Mastery Radar Chart",
      "Certified Scholar Badges",
      "Daily Quests & Scholar Dollars Engine",
      "Digital Scholar ID Card"
    ],
    expandedBenefits: [
      "The Combine: our sports-centric IXL — endless AI practice that never runs out",
      "Train your way: All-Around, single subject, Today's Lesson, or Target-a-Weakness",
      "Gamified with live streaks, 'on-fire' hot streaks, coach hints & Scholar Dollars",
      "Practice feeds your Mastery Radar, badges & quests",
      "Stay sharp — works standalone or alongside full-time enrollment"
    ],
    focus: "GameDay Skill Engine",
    popular: false
  },
  virtual_private_school: {
    name: 'Full Virtual Private School',
    price: 299,
    period: 'mo',
    icon: Star,
    color: '#FF5E00',
    features: [
      "Complete State-Registered Curriculum",
      "3-Hour Daily Station Rotation",
      "AI Socratic Tutor",
      "Automated Grading",
      "Official Transcript Generation",
      "Full access to The Combine (unlimited sports-based practice)"
    ],
    expandedBenefits: [
      "Everything in GameDay Skill Engine, plus:",
      "State-registered virtual private school",
      "Daily 3-hour station rotation matrix",
      "AI-coached open-response grading",
      "Official transcript generation",
      "Verifiable academic ledger & portfolio"
    ],
    focus: "Full Virtual Private School",
    popular: true
  },
  live_classroom_hybrid: {
    name: 'Live Classroom Hybrid Stream',
    price: 499,
    period: 'mo',
    icon: Crown,
    color: '#C9A961',
    features: [
      "Everything in Full Virtual Private School",
      "Daily Video Replay Vault",
      "Station 1 Direct Instruction Recordings",
      "Priority Teacher Support",
      "Live Classroom Streams"
    ],
    expandedBenefits: [
      "Everything in Full Virtual Private School, plus:",
      "Daily physical-classroom stream replays",
      "Station 1 direct instruction recordings",
      "Priority teacher support",
      "Verifiable academic ledger & portfolio"
    ],
    focus: "Live Classroom Hybrid Stream",
    popular: false,
    highlighted: true
  }
};

// Parent-specific tiers
const PARENT_TIERS = {
  parent_baseline: {
    name: 'Parent Observer',
    price: 0,
    period: 'included',
    icon: Zap,
    color: '#A855F7',
    features: [
      "Included with any student enrollment",
      "Proof of Presence gallery",
      "Basic academic reports",
      "Attendance monitoring"
    ],
    expandedBenefits: [
      "Biometric check-in timestamps",
      "Basic compliance tracking",
      "Email notifications",
      "Dashboard access"
    ],
    focus: "Included with Enrollment",
    popular: false
  },
  parent_strategist: {
    name: 'Parent Command Center',
    price: 499,
    period: 'month',
    icon: Star,
    color: '#8B5CF6',
    features: [
      "Full Proof of Presence biometric audit",
      "CA State Standards compliance tracking",
      "Athletic Eligibility monitoring",
      "Athletic scout profile access",
      "1-Click Counselor Escalation",
      "Financial Office"
    ],
    expandedBenefits: [
      "Everything in Observer, plus:",
      "Weekly video briefings on your athlete",
      "Parent coaching curriculum access",
      "Priority message response",
      "Group strategy call invites"
    ],
    focus: "Active Partnership",
    popular: true
  },
  parent_executive: {
    name: 'Parent Executive',
    price: 199,
    icon: Crown,
    color: '#7C3AED', // Deep Purple
    features: [
      "Everything in Strategist tier",
      "Priority 1-on-1 coaching calls",
      "Recruitment strategy sessions",
      "NIL and media guidance",
      "Transfer/transition advisory",
      "Direct academy leadership access"
    ],
    expandedBenefits: [
      "Everything in Strategist, plus:",
      "Private 1-on-1 coaching sessions",
      "Personalized recruitment roadmap",
      "NIL opportunity guidance",
      "Direct line to academy directors"
    ],
    focus: "Full Partnership",
    popular: false,
    highlighted: true
  }
};

const AcademyEnroll = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTier, setSelectedTier] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showQuickCheckout, setShowQuickCheckout] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' or 'login'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState(null);
  const [expandedTier, setExpandedTier] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true); // Add auth checking state
  
  // Check if parent mode from URL params
  const isParentMode = searchParams.get('type') === 'parent';
  const activeTiers = isParentMode ? PARENT_TIERS : TIERS;
  const themeColor = isParentMode ? 'purple' : 'orange';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });

  const [quickCheckoutData, setQuickCheckoutData] = useState({
    name: '',
    email: ''
  });

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('academy_token');
    if (token) {
      checkAuth(token);
    } else {
      setCheckingAuth(false);
      
      // Check if ?mode=login is in URL - open login modal automatically
      const mode = searchParams.get('mode');
      if (mode === 'login') {
        setAuthMode('login');
        setShowAuthModal(true);
      }
    }
  }, [searchParams]);

  const checkAuth = async (token) => {
    try {
      const response = await api.get('/academy/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const user = response.data.user;
        setUser(user);
        
        // Check if already enrolled (is_student true, or has paid, or has subscription tier)
        const isEnrolled = user.is_student || user.payment_status === 'paid' || user.subscription_tier;
        
        if (isEnrolled) {
          window.location.href = '/academy/school';
          return;
        }
      }
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('academy_token');
      localStorage.removeItem('academy_user');
    } finally {
      setCheckingAuth(false);
      // After auth check, if mode=login and user isn't redirected, open modal
      const mode = searchParams.get('mode');
      if (mode === 'login') {
        setAuthMode('login');
        setShowAuthModal(true);
      }
    }
  };

  const handleSelectTier = (tierKey) => {
    setSelectedTier(tierKey);
    // Open quick checkout modal for guest users
    if (!user) {
      setShowQuickCheckout(true);
    } else {
      handleCheckout(tierKey);
    }
  };

  const toggleExpandedBenefits = (tierKey, e) => {
    e.stopPropagation();
    setExpandedTier(expandedTier === tierKey ? null : tierKey);
  };

  const handleQuickCheckout = async (e) => {
    e.preventDefault();
    if (!quickCheckoutData.name || !quickCheckoutData.email) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/academy/public-checkout', {
        tier: selectedTier,
        name: quickCheckoutData.name,
        email: quickCheckoutData.email,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = authMode === 'signup' ? '/academy/auth/signup' : '/academy/auth/login';
      const payload = authMode === 'signup' 
        ? formData 
        : { email: formData.email, password: formData.password };

      const response = await api.post(endpoint, payload);

      if (response.data.success) {
        // Store auth data first
        localStorage.setItem('academy_token', response.data.token);
        localStorage.setItem('academy_user', JSON.stringify(response.data.user));
        
        toast.success(authMode === 'signup' ? 'Account created!' : 'Welcome back!');
        
        // Check if user is enrolled (is_student true, or has paid, or has subscription tier)
        const user = response.data.user;
        const isEnrolled = user.is_student || user.payment_status === 'paid' || user.subscription_tier;
        
        if (isEnrolled) {
          // Use window.location.href for hard redirect to ensure full page reload
          window.location.href = '/academy/school';
          return;
        }
        
        // Update local state for non-enrolled users
        setUser(response.data.user);
        setShowAuthModal(false);
        
        // Otherwise, proceed to checkout if tier selected
        if (selectedTier) {
          handleCheckout(selectedTier);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (tierKey) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('academy_token');
      const response = await api.post('/academy/checkout', 
        { tier: tierKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth to prevent flash of content
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white pt-16" style={{ backgroundColor: '#050508' }}>
      {/* Hero Section */}
      <div className="relative py-16 px-6" style={{ background: 'linear-gradient(to bottom, #0A0A0E, #050508)' }}>
        {/* Grid background */}
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${isParentMode ? '123,44,191' : '255,94,0'},0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(${isParentMode ? '123,44,191' : '255,94,0'},0.08) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative z-10 container mx-auto text-center">
          <img src={isParentMode ? '/images/scholar-parent-logo-new.png' : '/images/scholar-athlete-logo-new.png'}
            alt="SAA" className="mx-auto h-24 md:h-32 w-auto mb-6" style={{ filter: `drop-shadow(0 0 20px rgba(${isParentMode ? '123,44,191' : '255,94,0'},0.3))` }} />
          <h1 className="text-3xl md:text-5xl font-black mb-4 tracking-tight uppercase">
            {isParentMode ? (
              <>Select Your <span style={{ color: '#7B2CBF' }}>Command Level</span></>
            ) : (
              <>Apply For Your <span style={{ color: '#FF5E00' }}>Seat</span></>
            )}
          </h1>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto mb-4">
            {isParentMode
              ? 'Choose the level of visibility and control over your child\'s academic and athletic journey.'
              : 'AI-powered curriculum. Biometric attendance. CIF / high-school eligibility tracking. 1-week free trial included with full enrollment.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
            <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
              <Shield className="w-3 h-3" style={{ color: '#C9A961' }} />
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400">California State Standards Aligned</span>
            </div>
            <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5" data-testid="state-compliance-badge">
              <Shield className="w-3 h-3" style={{ color: '#C9A961' }} />
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400">Compliant In Every State We Operate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="container mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px max-w-6xl mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
          {Object.entries(activeTiers).map(([key, tier]) => {
            const Icon = tier.icon;
            const isExpanded = expandedTier === key;
            const isElite = key === 'live_classroom_hybrid' || key === 'parent_executive';
            const themeColor = isParentMode ? '#7B2CBF' : '#FF5E00';
            
            return (
              <Card 
                key={key}
                className="border-0 rounded-none transition-all duration-300 relative overflow-hidden"
                style={{
                  backgroundColor: '#050508',
                  borderTop: tier.popular
                    ? `2px solid ${isParentMode ? '#A855F7' : '#F97316'}`
                    : isElite
                      ? '2px solid #F59E0B'
                      : '2px solid transparent'
                }}
              >
                {tier.popular && (
                  <div 
                    className="absolute top-0 right-0 text-black px-4 py-1 text-xs font-black uppercase tracking-wider"
                    style={{ backgroundColor: isParentMode ? '#A855F7' : '#F97316' }}
                  >
                    Most Popular
                  </div>
                )}
                {isElite && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-4 py-1 text-xs font-black uppercase tracking-wider">
                    {isParentMode ? 'Full Partnership' : 'Full Experience'}
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${tier.color}20`, border: `2px solid ${tier.color}` }}
                  >
                    <Icon className="w-8 h-8" style={{ color: tier.color }} />
                  </div>
                  <CardTitle className="text-2xl font-black text-white">{tier.name}</CardTitle>
                  <p className="text-sm text-gray-500 uppercase tracking-wider">{tier.focus}</p>
                </CardHeader>
                
                <CardContent className="text-center">
                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-5xl font-black text-white">${tier.price}</span>
                    <span className="text-gray-500 ml-2">/{tier.period || 'mo'}</span>
                  </div>
                  
                  {/* Features List */}
                  <ul className="space-y-3 mb-6 text-left">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* View Full Benefits Dropdown */}
                  <button
                    onClick={(e) => toggleExpandedBenefits(key, e)}
                    className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-sm font-semibold transition-colors"
                    style={{ color: tier.color }}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Hide Benefits
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        View Full Roster of Benefits
                      </>
                    )}
                  </button>
                  
                  {/* Expanded Benefits */}
                  {isExpanded && (
                    <div className="mb-6 p-4 rounded-lg border animate-in slide-in-from-top-2 duration-200"
                      style={{ 
                        backgroundColor: `${tier.color}10`, 
                        borderColor: `${tier.color}30` 
                      }}
                    >
                      <ul className="space-y-2 text-left">
                        {tier.expandedBenefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <div 
                              className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                              style={{ backgroundColor: tier.color }}
                            />
                            <span className="text-gray-300 text-sm">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* CTA Button */}
                  <Button 
                    onClick={() => handleSelectTier(key)}
                    disabled={loading}
                    className={`w-full font-bold py-6 text-base md:text-lg transition-all whitespace-nowrap px-4 min-h-[60px] flex items-center justify-center ${
                      tier.popular
                        ? 'bg-orange-500 hover:bg-orange-600 text-black'
                        : isElite
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black'
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                    }`}
                  >
                    {loading && selectedTier === key ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span className="truncate">Join {tier.name}</span>
                        <ArrowRight className="w-5 h-5 flex-shrink-0" />
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Full VPS / Hybrid Highlight Banner */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-amber-900/30 to-amber-800/20 border border-amber-500/30 rounded-xl p-6 text-center">
            <Crown className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h3 className="text-xl font-black text-amber-400 mb-2">California Standards-Aligned Private School</h3>
            <p className="text-gray-400 text-sm max-w-2xl mx-auto">
              The <span className="text-amber-400 font-bold">Full Virtual Private School</span> and{' '}
              <span className="text-amber-400 font-bold">Live Classroom Hybrid Stream</span> tiers deliver a
              complete, California-standards-aligned curriculum with official transcripts and a verifiable academic ledger.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Checkout Modal */}
      <Dialog open={showQuickCheckout} onOpenChange={setShowQuickCheckout}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-center">
              Quick Checkout
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleQuickCheckout} className="space-y-4 mt-4">
            {selectedTier && (
              <div className="bg-gray-800 rounded-lg p-4 text-center mb-4">
                <p className="text-sm text-gray-400">Selected Plan</p>
                <p className="text-xl font-bold text-orange-500">{TIERS[selectedTier]?.name} - ${TIERS[selectedTier]?.price}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="quick-name" className="text-gray-300">Student Name</Label>
              <Input
                id="quick-name"
                value={quickCheckoutData.name}
                onChange={(e) => setQuickCheckoutData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                placeholder="Full name"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="quick-email" className="text-gray-300">Email</Label>
              <Input
                id="quick-email"
                type="email"
                value={quickCheckoutData.email}
                onChange={(e) => setQuickCheckoutData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue to Payment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              Already have an account?{' '}
              <button 
                type="button"
                onClick={() => {
                  setShowQuickCheckout(false);
                  setAuthMode('login');
                  setShowAuthModal(true);
                }}
                className="text-orange-500 hover:underline"
              >
                Log in here
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-center">
              {authMode === 'signup' ? 'Create Account' : 'Welcome Back'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAuth} className="space-y-4 mt-4">
            {authMode === 'signup' && (
              <div>
                <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                  required
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-gray-800 border-gray-700 text-white mt-1 pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                authMode === 'signup' ? 'Create Account' : 'Log In'
              )}
            </Button>
            
            <p className="text-sm text-gray-400 text-center">
              {authMode === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <button 
                    type="button"
                    onClick={() => setAuthMode('login')}
                    className="text-orange-500 hover:underline"
                  >
                    Log in
                  </button>
                </>
              ) : (
                <>
                  Need an account?{' '}
                  <button 
                    type="button"
                    onClick={() => setAuthMode('signup')}
                    className="text-orange-500 hover:underline"
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademyEnroll;
